import React, { lazy, Suspense, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import styles from './styles.module.scss';
import Heading from '../../shared/heading';
import Button from '../../shared/button';
// import useVideo from '../../../hooks/use-video';
import VolumeControl from './icons/VolumeControl';
import VolumeCrossedControl from './icons/VolumeCrossedControl';
import { useMedia } from 'react-use';
import cn from 'classnames';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const MediaHero = ({
  video = null,
  image = null,
  title,
  desc,
  buttonLabel = null,
  buttonLink = null,
  titleTag = 'h2',
  children = null,
}) => {
  // const { videoWrapperRef, videoRef, isVideoMuted, isUnmuteVisible, unmute, mute, handleVideoEnd } =
  //   useVideo();
  // const imageContainerRef = useRef(null);
  const wistiaPlayerRef = useRef(null);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // const appendImageToCard = useCallback((cardId) => {
  //   const imageDiv = document.getElementById(`img-${cardId}`);

  //   if (imageDiv && imageContainerRef.current) {
  //     imageContainerRef.current.appendChild(imageDiv);
  //   }
  // }, []);

  // Handle image appending for this specific card (used as static poster and image-only variant)
  // useEffect(() => {
  //   if (image?.id) {
  //     appendImageToCard(image.id);
  //   }
  // }, [image, appendImageToCard]);

  const isMobile = useMedia('(max-width: 767px)', false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Use useState+useEffect instead of useMemo+window to avoid SSR/client
  // value mismatch (hydration error #425). Always start with 844 (server value),
  // then update after mount based on real viewport.
  const [playerHeight, setPlayerHeight] = useState(undefined);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 767) {
        setPlayerHeight(420);
      } else if (window.innerWidth < 1024) {
        setPlayerHeight(700);
      } else if (window.innerWidth < 1880) {
        setPlayerHeight(600);
      } else {
        setPlayerHeight(844);
      }
    }
  }, []);

  const [videoContainerRef, inView] = useInView({ threshold: 0.4 });

  const placeStyleToWistia = useCallback((wistiaElementParam) => {
    const wistiaElement = wistiaElementParam || wistiaPlayerRef.current;
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
            /* This CSS is now safely inside the Shadow DOM */
            video {
              object-fit: cover !important;
            }
            .click-for-sound-btn {
              display: none !important;
            }
        `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (event) => {
      const player = event?.target;
      if (!player) return;
      wistiaPlayerRef.current = player;
      placeStyleToWistia(player);
      try {
        setIsVideoMuted(!!player.muted);
      } catch (e) {
        console.error(e);
      }
      setIsVideoReady(true);
    },
    [placeStyleToWistia]
  );

  const toggleMute = useCallback(() => {
    const player = wistiaPlayerRef.current;
    if (!player) return;
    try {
      const nextMuted = !player.muted;
      player.muted = nextMuted;
      setIsVideoMuted(nextMuted);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    const player = wistiaPlayerRef.current;
    if (!player) return;

    if (inView) {
      player.play?.();
    } else {
      player.pause?.();
    }

    return () => {
      player.pause?.();
    };
  }, [inView]);
  const showOverlay = useMemo(
    () => !!title || !!desc || !!buttonLabel || !!buttonLink,
    [title, desc, buttonLabel, buttonLink]
  );

  if (!image && !video) {
    return null;
  }
  return (
    <section className={styles.headHero}>
      <div className={styles.mediaContainer}>
        {image && (
          <div
            className={`${styles.imageContainer} ${
              isVideoReady && video ? styles.imageHidden : ''
            }`}
            // ref={imageContainerRef}
          >
            {children}
          </div>
        )}
        {showOverlay && (
          <div className={styles.overlay}>
            <div className={styles.inner}>
              <div className={styles.infoBox}>
                {title && (
                  <Heading className={styles.title} tag={titleTag} size="xl">
                    {title}
                  </Heading>
                )}
                {desc && (!isClient || !isMobile) && <p>{desc}</p>}
                {buttonLabel && buttonLink && (!isClient || !isMobile) && (
                  <div className={styles.btnBox}>
                    <Button to={buttonLink} theme="accent-primary">
                      {buttonLabel}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <Button
              className={styles.soundToggleButton}
              theme="primary"
              size="sm"
              data-type="video-sound-button"
              aria-label={isVideoMuted ? 'Unmute video' : 'Mute video'}
              onClick={toggleMute}
            >
              {isVideoMuted ? <VolumeCrossedControl /> : <VolumeControl />}
            </Button>
          </div>
        )}
        {isClient && video && (
          <div
            className={cn(styles.videoContainer, { [styles.visible]: isVideoReady })}
            ref={videoContainerRef}
          >
            <Suspense fallback={null}>
              {playerHeight && (
                <WistiaPlayerLazy
                  mediaId={video}
                  ref={wistiaPlayerRef}
                  onApiReady={handleApiReady}
                  autoPlay
                  playlistLoop
                  muted={isVideoMuted}
                  className={styles.wistiaPlayer}
                  poster={false}
                  swatch={false}
                  resumable
                  style={{
                    height: playerHeight,
                  }}
                />
              )}
            </Suspense>
          </div>
        )}
        {/* {image && !video && <div className={styles.imageContainer} ref={imageContainerRef} />} */}
      </div>
      {isClient && isMobile && (
        <div className={styles.mobileInfoBox}>
          {desc && <p>{desc}</p>}
          {buttonLabel && buttonLink && (
            <div className={styles.btnBox}>
              <Button to={buttonLink} theme="accent-primary">
                {buttonLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

MediaHero.propTypes = {
  video: PropTypes.string,
  image: PropTypes.shape({
    id: PropTypes.number,
  }),
  title: PropTypes.string,
  desc: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  children: PropTypes.node,
};

export default MediaHero;
